/**
 * Oversikt → Perioden (#273 v3.1, HANDOFF «Perioden»): the month as Planlegg → Kontroller → Rapporter, then
 * staffing cost against sales and the company's own rule checks. Every figure is read here from the API
 * (period.ts); a stage is never marked done by a visit or a closed guide (OV3-02). The month before is named
 * above the stages while it is not closed out, so a new empty month does not hide it (OV3-01). Each link
 * carries its month into the page that owns the work. Nothing here writes.
 */
import { useAbsenceTypes, useAbsences, useShifts } from '@/api/data';
import { useLabour, useLabourCost } from '@/api/labour';
import { useCan } from '@/auth/caps';
import { T } from '@/i18n';
import { dayEntries, oslo, tenantRules, toPerson } from '@/rules';
import { useTenant } from '@/shell/tenant';
import { Skeleton } from '@/ui';
import { MONTHS, NO_VALUE, fullName, monthLabel, num } from '@/ui/format';
import { Icon } from '@/ui/icons';
import { Status } from '@/ui/page';
import { addDays, mondayOf } from '@timefly/core';
import type { ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { kindOfAbsence } from '../fravaer/AbsenceRange';
import { usePayMonth, when } from '../lonn/Month';
import { lastDayOf, prevMonth } from '../timeliste/summary';
import { toVaktplan } from '../vaktplan/link';
import { ReadNotice } from './ReadNotice';
import { closeout, controlFacts, economy, inMonth, planFacts, reportState } from './period';

const kr = (n: number) => `${Math.round(n).toLocaleString('nb-NO')} kr`;
const plural = (n: number, one: string, many: string) => (n === 1 ? T(one, { n }) : T(many, { n }));
const monthName = (ym: string) => MONTHS[Number(ym.slice(5, 7)) - 1] ?? ym;
const cap = (s: string) => s.charAt(0).toUpperCase() + s.slice(1);

export function PeriodenTab({
  ym,
  today,
  now,
  onMonth,
  queueLink,
}: {
  ym: string;
  today: string;
  now: number;
  onMonth: (ym: string) => void;
  queueLink: string;
}) {
  const can = useCan();
  const { tenant, mode, workspace } = useTenant();
  const rota = mode === 'rota' && workspace.enabled.scheduling;
  const seesPlan = rota && can('vaktplan.se');
  const seesPay = can('timerloenn.se');
  const seesCost = can('vaktplan.kost');
  const prev = prevMonth(ym);
  const last = lastDayOf(ym);
  // one read for both months, from the day before the month before (a night shift into its first day)
  const shiftsQ = useShifts(addDays(`${prev}-01`, -1), last);
  const labour = useLabour();
  const absQ = useAbsences();
  const typesQ = useAbsenceTypes(can('fravaer.se'));
  const payQ = usePayMonth(ym, seesPay);
  const prevPayQ = usePayMonth(prev, seesPay);
  const cost = useLabourCost(`${ym}-01`, last, seesCost);
  const tol = tenantRules().toleranceMin ?? 15;

  const shifts = shiftsQ.data?.shifts;
  const absenceKnown =
    absQ.data !== undefined && !absQ.isError && typesQ.data !== undefined && !typesQ.isError;
  const plan =
    shifts && labour.settings !== undefined
      ? planFacts(shifts, ym, today, labour.need, absenceKnown ? (absQ.data ?? []) : null, (a) =>
          kindOfAbsence(a, typesQ.data ?? []),
        )
      : null;
  const ctl = shifts ? controlFacts(shifts, ym, now, tol) : null;
  const prevCtl = shifts ? controlFacts(shifts, prev, now, tol) : null;
  const close = closeout(prevCtl, prevPayQ.data, seesPay);
  const rep = reportState(ctl, payQ.data);
  const running = ym >= today.slice(0, 7);
  // the company's own rule checks on the month's shifts, as Vaktplan marks them (sev d, an open punch apart)
  const rules = tenantRules();
  const warned =
    shifts && tenant
      ? shifts.filter(
          (s) =>
            inMonth(s, ym) &&
            s.user &&
            dayEntries(s, toPerson(s.user, tenant), rules).some((e) => e.sev === 'd' && e.code !== 'open'),
        )
      : null;
  const eco = seesCost && cost.ready ? economy(cost.days, ym, today) : null;
  const firstGap = plan?.gapDays[0];

  const prevName = cap(monthName(prev));
  return (
    <div className="oxper" data-testid="ov-period">
      <ReadNotice
        reads={[
          [T('vaktene'), shiftsQ],
          ...(seesPlan ? [[T('bemanningsbehov'), labour.query] as [string, typeof labour.query]] : []),
          ...(seesPay ? [[T('lønnsmåneden'), payQ] as [string, typeof payQ]] : []),
        ]}
      />
      {close && !close.done && (
        <section className="oxclose" aria-labelledby="ov-close" data-testid="ov-closeout">
          <Icon name="calendar" />
          <div>
            <h2 id="ov-close">{T('{month} er ikke avsluttet', { month: prevName })}</h2>
            <p>
              {[
                close.left > 0 &&
                  plural(close.left, '{n} vakt er ikke godkjent', '{n} vakter er ikke godkjent'),
                close.unexported && T('godkjente timer er ikke eksportert'),
                close.changed &&
                  (close.people?.length
                    ? T('endret etter eksport for {names}', {
                        names: close.people.map((p) => p.name).join(', '),
                      })
                    : T('timene er endret etter eksport')),
              ]
                .filter(Boolean)
                .join(', ')}
            </p>
          </div>
          <button type="button" className="btn" onClick={() => onMonth(prev)}>
            {T('Fullfør {month}', { month: monthName(prev) })}
            <Icon name="chevronRight" />
          </button>
        </section>
      )}
      <div className="oxjourney">
        {seesPlan && (
          <Stage
            n={1}
            icon="calendar"
            title={T('Planlegg')}
            status={
              !plan
                ? null
                : plan.days === 0
                  ? ['neu', T('Perioden er over')]
                  : plan.gaps + plan.open + plan.drafts > 0
                    ? ['warn', T('Gjenstår')]
                    : plan.hasNeed
                      ? ['ok', T('Dekket')]
                      : ['neu', T('Behov ikke satt')]
            }
            head={
              !plan
                ? NO_VALUE
                : plan.days === 0
                  ? T('Ingen dager igjen å planlegge')
                  : plan.gaps
                    ? plural(plan.gaps, '{n} tidsrom under behov', '{n} tidsrom under behov')
                    : plan.open
                      ? plural(plan.open, '{n} ledig vakt', '{n} ledige vakter')
                      : T('Planen dekker behovet')
            }
            lines={
              plan && plan.days > 0
                ? [
                    plan.hasNeed
                      ? plural(plan.gapDays.length, 'Under behov på {n} dag', 'Under behov på {n} dager')
                      : T('Behovet er ikke satt, så dekningen kan ikke vurderes'),
                    plural(plan.open, '{n} ledig vakt uten ansatt', '{n} ledige vakter uten ansatt'),
                    plural(plan.drafts, '{n} vakt er ikke publisert', '{n} vakter er ikke publisert'),
                    T('Fra {from} til månedens slutt', {
                      from: `${Number((today > `${ym}-01` ? today : `${ym}-01`).slice(8))}.`,
                    }),
                  ]
                : []
            }
            link={
              firstGap
                ? { to: `/hjem?d=${firstGap.date}`, label: T('Se hva som mangler') }
                : {
                    to: toVaktplan({ week: mondayOf(today > `${ym}-01` ? today : `${ym}-01`) }),
                    label: T('Åpne vaktplan'),
                  }
            }
          />
        )}
        <Stage
          n={seesPlan ? 2 : 1}
          icon="clock"
          title={T('Kontroller')}
          status={
            !ctl
              ? null
              : ctl.action + ctl.ready > 0
                ? ['warn', T('Gjenstår')]
                : ctl.ended > 0
                  ? ['ok', T('Godkjent')]
                  : ['neu', T('Ingen ennå')]
          }
          head={
            !ctl
              ? NO_VALUE
              : ctl.action + ctl.ready > 0
                ? plural(ctl.action + ctl.ready, '{n} registrering gjenstår', '{n} registreringer gjenstår')
                : ctl.ended > 0
                  ? T('Alle registreringer er godkjent')
                  : T('Ingen avsluttede vakter ennå')
          }
          lines={
            ctl
              ? [
                  T('{n} av {of} avsluttede vakter godkjent', { n: ctl.approved, of: ctl.ended }),
                  ...(ctl.missing + ctl.open
                    ? [plural(ctl.missing + ctl.open, '{n} mangler stempling', '{n} mangler stempling')]
                    : []),
                  ...(ctl.deviation
                    ? [plural(ctl.deviation, '{n} avvik fra plan', '{n} avvik fra plan')]
                    : []),
                  ...(ctl.ready
                    ? [plural(ctl.ready, '{n} klar til godkjenning', '{n} klare til godkjenning')]
                    : []),
                  T('Å rette en registrering godkjenner den ikke.'),
                ]
              : []
          }
          link={{
            to: `/timeliste?vis=${ctl?.action ? 'handling' : 'klare'}&mnd=${ym}`,
            label: T('Åpne kontroll'),
          }}
        />
        {seesPay && (
          <Stage
            n={seesPlan ? 3 : 2}
            icon="banknote"
            title={T('Rapporter')}
            status={
              rep === 'unknown'
                ? null
                : rep === 'changed'
                  ? ['warn', T('Endret etter eksport')]
                  : rep === 'closed'
                    ? ['ok', T('Lukket')]
                    : rep === 'exported'
                      ? ['ok', T('Eksportert')]
                      : rep === 'ready'
                        ? ['act', T('Klar til eksport')]
                        : ['neu', T('Ikke klart')]
            }
            head={ctl ? T('{h} t godkjent', { h: num(ctl.approvedHours, 1) }) : NO_VALUE}
            lines={[
              T('Bare godkjente timer er med i grunnlaget.'),
              ...(running
                ? [
                    T('Perioden pågår, til og med {day}.', {
                      day: `${Number(today.slice(8))}. ${monthName(today.slice(0, 7))}`,
                    }),
                  ]
                : []),
              ...(payQ.data?.last_export
                ? [
                    `${T('Siste eksport')} ${when(payQ.data.last_export.at)}, ${payQ.data.last_export.by_name}. ${T('Import og lønnskjøring er ikke bekreftet her.')}`,
                  ]
                : payQ.data
                  ? [T('Ikke eksportert')]
                  : []),
              ...(rep === 'changed'
                ? [
                    payQ.data?.changed_people?.length
                      ? T('Endret for: {names}', {
                          names: payQ.data.changed_people.map((p) => p.name).join(', '),
                        })
                      : T('Timene er endret etter siste eksport.'),
                  ]
                : []),
              T('Lønnsarter og ansattnummer kontrolleres i Rapporter før filen lages.'),
            ]}
            link={{ to: `/lonn?mnd=${ym}`, label: T('Se lønnsgrunnlag') }}
          />
        )}
      </div>

      {seesCost && (
        <section className="oxs" aria-labelledby="ov-eco" data-testid="ov-economy">
          <div className="oxsh">
            <div>
              <h2 id="ov-eco">{T('Bemanning og økonomi')}</h2>
              <p className="oxsub">
                {monthLabel(ym)}
                {running ? `, ${T('til og med {day}', { day: `${Number(today.slice(8))}.` })}` : ''},{' '}
                {T('alle team')}
              </p>
            </div>
          </div>
          {!eco ? (
            cost.ready ? null : (
              <div className="oxpad">
                <Skeleton rows={2} />
              </div>
            )
          ) : (
            <>
              <dl className="oxfig">
                <div>
                  <dt>{T('Planlagt lønnskostnad')}</dt>
                  <dd className="tnum">{kr(eco.planned)}</dd>
                  <small>{T('Hele måneden, timer × satser med tillegg')}</small>
                </div>
                <div>
                  <dt>{T('Registrert lønnskostnad')}</dt>
                  <dd className="tnum">{kr(eco.recorded)}</dd>
                  <small>{T('Stemplede timer så langt')}</small>
                </div>
                <div>
                  <dt>{T('Registrert omsetning')}</dt>
                  <dd className="tnum">{eco.sales == null ? T('Ikke registrert') : kr(eco.sales)}</dd>
                  <small>{T('{n} av {of} avsluttede dager', { n: eco.salesDays, of: eco.pastDays })}</small>
                </div>
                <div>
                  <dt>{T('Lønnsprosent')}</dt>
                  <dd className="tnum">{eco.pct == null ? T('Ukjent') : `${num(eco.pct, 1)} %`}</dd>
                  <small>
                    {eco.pct == null
                      ? T('Ingen dager med både omsetning og stempling')
                      : plural(
                          eco.matchedDays,
                          '{n} dag med både omsetning og stempling',
                          '{n} dager med både omsetning og stempling',
                        )}
                    {cost.target != null &&
                      eco.pct != null &&
                      `, ${T('mål {pct} %', { pct: num(cost.target, 1) })}`}
                  </small>
                </div>
              </dl>
              <p className="oxnote">
                <Icon name="info" />
                <span>
                  {T(
                    'Lønnsprosent er registrert lønnskostnad delt på omsetning, bare for dager med begge deler. Den viser bemanningskostnad, ikke virksomhetens overskudd.',
                  )}
                  {eco.unpriced > 0 &&
                    ` ${plural(eco.unpriced, '{n} vakt uten timelønn er ikke med i kostnaden.', '{n} vakter uten timelønn er ikke med i kostnaden.')}`}
                </span>
              </p>
              <div className="oxsf">
                <span>{T('Se hvilke dager som bidrar.')}</span>
                <span className="sp" />
                <Link className="mlink" to={`/omsetning?mnd=${ym}`}>
                  {T('Se kostnad mot omsetning')}
                  <Icon name="chevronRight" />
                </Link>
              </div>
            </>
          )}
        </section>
      )}

      <div className="oxpair">
        <section className="oxs" aria-labelledby="ov-rules" data-testid="ov-rules">
          <div className="oxsh">
            <div>
              <h2 id="ov-rules">{T('Arbeidstid og hvile')}</h2>
              <p className="oxsub">
                {warned == null
                  ? NO_VALUE
                  : warned.length
                    ? plural(warned.length, '{n} vakt med regelvarsel', '{n} vakter med regelvarsel')
                    : T('Ingen regelvarsler i {month}', { month: monthName(ym) })}
              </p>
            </div>
          </div>
          {warned && warned.length > 0 && (
            <p className="oxpl">
              {warned
                .slice(0, 3)
                .map((s) => `${fullName(s.user)} ${Number(oslo(s.start_time)?.date.slice(8) ?? 0)}.`)
                .join(', ')}
            </p>
          )}
          <p className="oxpl oxmute">
            {T(
              'Varslene er bedriftens egne kontroller av vaktene. Ingen varsel er ingen bekreftelse på at arbeidsmiljøloven er fulgt.',
            )}
          </p>
          {seesPlan && warned && warned.length > 0 && (
            <Link
              className="mlink"
              to={toVaktplan({ week: mondayOf(oslo(warned[0]?.start_time)?.date ?? today) })}
            >
              {T('Se vaktene')}
              <Icon name="chevronRight" />
            </Link>
          )}
        </section>
        <section className="oxs" aria-labelledby="ov-more">
          <div className="oxsh">
            <div>
              <h2 id="ov-more">{T('Arbeid fra andre dager')}</h2>
              <p className="oxsub">
                {T('Oppfølging samler saker fra denne og forrige måned, med egen dato.')}
              </p>
            </div>
          </div>
          <Link className="mlink" to={queueLink}>
            {T('Alle oppfølgingspunkter')}
            <Icon name="chevronRight" />
          </Link>
        </section>
      </div>
    </div>
  );
}

function Stage({
  n,
  icon,
  title,
  status,
  head,
  lines,
  link,
}: {
  n: number;
  icon: 'calendar' | 'clock' | 'banknote';
  title: string;
  /** null while its reads are missing: no status is invented */
  status: ['ok' | 'warn' | 'neu' | 'act', string] | null;
  head: ReactNode;
  lines: string[];
  link: { to: string; label: string };
}) {
  return (
    <section className="oxstage" aria-labelledby={`ov-stage-${n}`} data-testid="ov-stage">
      <div className="oxstageh">
        <h2 id={`ov-stage-${n}`}>
          <Icon name={icon} />
          {title}
          <span className="oxstagen" aria-hidden="true">
            {n}
          </span>
        </h2>
        {status ? <Status tone={status[0]}>{status[1]}</Status> : <span className="oxmute">{NO_VALUE}</span>}
      </div>
      <p className="oxstagehead">{head}</p>
      <ul>
        {lines.map((l) => (
          <li key={l}>{l}</li>
        ))}
      </ul>
      <Link className="mlink" to={link.to}>
        {link.label}
        <Icon name="chevronRight" />
      </Link>
    </section>
  );
}
